import { useEffect, useRef, useState } from 'react';
import { api } from '../../../api';
import type { BasilHttp } from '../write/writeTypes';
import { NO_TITLES, readBackgroundLabels, readBookAudio } from './readBookMedia';
import type { TitleRead } from './readBookMedia';

/** The titles the margin needs, and whether each read has finished deciding what they are. */
export interface BookMediaRead {
  /** the book's sounds and music, by audio id. */
  audio: TitleRead;
  /** the labels of the backgrounds this episode's locations use, by background id. */
  backgrounds: TitleRead;
  /** true once both reads have answered, however they answered. */
  settled: boolean;
}

export interface UseBookMediaOptions {
  /** the modal is open; a closed modal reads nothing. */
  enabled: boolean;
  /** the background ids this episode's locations actually use. An empty list reads none. */
  backgroundIds: readonly string[];
  /** the http seam; defaults to the page's axios instance. Tests pass a mock. */
  http?: BasilHttp;
}

const NOTHING: BookMediaRead = Object.freeze({
  audio: NO_TITLES,
  backgrounds: NO_TITLES,
  settled: false,
}) as BookMediaRead;

/**
 * Basil v0.2, P3 — the titles behind the margin labels, read once per open.
 *
 * The audio list goes out with the episode read. The background labels cannot: which backgrounds
 * this episode uses is only known once the LOCATIONS read has answered, so that request waits on
 * the ids and goes out as one batch when they arrive.
 *
 * Nothing waits for this hook. The script is decompiled, analysed, edited and applied whether or
 * not a single title ever lands; a slow or dead list costs the margin its words and costs the
 * writer nothing else. A newer open abandons an older read through the effect's own latch.
 */
export function useBookMedia(bookId: number, options: UseBookMediaOptions): BookMediaRead {
  const { enabled, backgroundIds, http } = options;
  const [state, setState] = useState<BookMediaRead>(NOTHING);
  const httpRef = useRef<BasilHttp>(http || (api as unknown as BasilHttp));

  httpRef.current = http || (api as unknown as BasilHttp);
  // The ids are a fresh array on every render; the effect must key on what they SAY, not on which
  // array object said it, or the background read would reissue on every keystroke.
  const wanted = Array.from(new Set(backgroundIds.map(String))).sort().join(',');

  useEffect(() => {
    if (!enabled) {
      setState(NOTHING);

      return undefined;
    }
    let live = true;
    const ids = wanted === '' ? [] : wanted.split(',');

    setState(NOTHING);
    Promise.all([
      readBookAudio(httpRef.current, bookId),
      readBackgroundLabels(httpRef.current, ids),
    ])
      .then(([audio, backgrounds]) => {
        if (live) {
          setState({ audio, backgrounds, settled: true });
        }
      })
      .catch(() => {
        if (live) {
          setState({ audio: NO_TITLES, backgrounds: NO_TITLES, settled: true });
        }
      });

    return () => {
      live = false;
    };
  }, [enabled, bookId, wanted]);

  return state;
}
